import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Plus, FileText, User, Edit } from 'lucide-react';
import { VacationRequestDialog } from './VacationRequestDialog';
import { AdministrativeRequestDialog } from './AdministrativeRequestDialog';
import { VoucherModal } from './VoucherModal';
import { ProgressiveDaysDialog } from './ProgressiveDaysDialog';
import { EditEmployeeDialog } from './EditEmployeeDialog';
import { formatChileanDate } from '../../utils/dateUtils';

export const EmployeeCard = ({ employee, onAddDays, onViewDetails, onUpdateEmployee, onRefreshEmployees }) => {
  const [currentRequest, setCurrentRequest] = useState(null);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [vacationRequestOpen, setVacationRequestOpen] = useState(false);
  const [administrativeRequestOpen, setAdministrativeRequestOpen] = useState(false);
  const [progressiveDaysOpen, setProgressiveDaysOpen] = useState(false);
  const [editEmployeeOpen, setEditEmployeeOpen] = useState(false);
  
  const availableDays = employee.vacation_days_available ?? employee.vacationDaysAvailable ?? 0;
  const availableAdminDays = employee.admin_days_available ?? employee.adminDaysAvailable ?? 0;
  const progressiveCurrentPeriod = employee.progressive_days_current_period ?? employee.progressiveDaysCurrentPeriod ?? 0;
  const progressiveTotalsSinceApproval = employee.progressive_days_total_since_approval ?? employee.progressiveDaysTotalSinceApproval ?? 0;
  
  const getAvailableStatusColor = () => {
    if (availableDays <= 0) return '#9CA3AF'; // text-gray-400
    if (availableDays <= 5) return '#F59E0B'; // text-amber-500/orange-500
    return '#10B981'; // text-emerald-500/success
  };

  const getAdminStatusColor = () => {
    if (availableAdminDays <= 0) return '#9CA3AF'; // text-gray-400
    return '#3B82F6'; // text-blue-500/accent
  };

  const years = Math.floor((Date.now() - new Date(employee.start_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000));

  const handleRequestCreated = async (request) => {
    setCurrentRequest(request);
    setVoucherModalOpen(true);
    // Refrescar empleados para que la Card muestre de inmediato los días disponibles
    try {
      await onRefreshEmployees?.();
    } catch (e) {
      // opcional: log
      console.warn('No se pudo refrescar empleados desde EmployeeCard:', e);
    }
  };

  
  return (
    <Card style={{
      background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
      borderRadius: '14px',
      border: '1px solid rgba(59,130,246,0.08)',
      boxShadow: '0 10px 25px rgba(2, 6, 23, 0.08)',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      backdropFilter: 'saturate(120%) blur(2px)'
    }}
    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 16px 32px rgba(2, 6, 23, 0.12)'}
    onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 10px 25px rgba(2, 6, 23, 0.08)'}
    >
      {/* Días Disponibles - Grande arriba */}
      <div style={{
        background: 'linear-gradient(90deg, rgba(59,130,246,0.08), rgba(37,99,235,0.06))',
        padding: '20px',
        borderBottom: '1px solid rgba(59,130,246,0.08)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '34px', 
              fontWeight: 800, 
              color: getAvailableStatusColor(),
              lineHeight: '1',
              textShadow: '0 1px 0 rgba(255,255,255,0.4)'
            }}>
              {availableDays.toFixed(2)}
            </div>
            <p style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#6B7280',
              marginTop: '4px'
            }}>
              Días Vacaciones
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '34px', 
              fontWeight: 800, 
              color: getAdminStatusColor(),
              lineHeight: '1',
              textShadow: '0 1px 0 rgba(255,255,255,0.4)'
            }}>
              {availableAdminDays}
            </div>
            <p style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#6B7280',
              marginTop: '4px'
            }}>
              Días Administrativos
            </p>
          </div>
        </div>
      </div>

  <CardHeader style={{ paddingBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <CardTitle style={{ 
              fontSize: '18px', 
              fontWeight: 700, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '4px',
              color: '#0F172A'
            }}>
              <User size={18} style={{ color: '#3B82F6' }} />
              {employee.name}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditEmployeeOpen(true)}
                style={{
                  padding: '4px',
                  width: '28px',
                  height: '28px',
                  marginLeft: 'auto',
                  color: '#64748B',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#EEF2FF';
                  e.currentTarget.style.color = '#1D4ED8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#64748B';
                }}
                title="Editar empleado"
              >
                <Edit size={14} />
              </Button>
            </CardTitle>
            <p style={{ 
              fontSize: '14px', 
              color: '#6B7280', 
              fontWeight: 500 
            }}>
              {employee.position}
            </p>
          </div>
          <Badge variant="secondary" style={{ 
            fontSize: '12px',
            backgroundColor: '#EEF2FF',
            color: '#1D4ED8',
            padding: '2px 8px',
            borderRadius: '9999px'
          }}>
            {years} {years === 1 ? 'año' : 'años'}
          </Badge>
        </div>
      </CardHeader>
      {/* Mini métricas de progresivos */}
      <div style={{ padding: '0 16px 8px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px'
        }}>
          <div style={{
            background: 'linear-gradient(180deg, #F8FAFC, #F1F5F9)',
            border: '1px solid rgba(59,130,246,0.08)',
            borderRadius: '8px',
            padding: '8px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '12px', color: '#64748B' }}>Progresivos (período)</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937' }}>{progressiveCurrentPeriod}</span>
          </div>
          <div style={{
            background: 'linear-gradient(180deg, #F8FAFC, #F1F5F9)',
            border: '1px solid rgba(59,130,246,0.08)',
            borderRadius: '8px',
            padding: '8px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '12px', color: '#64748B' }}>Progresivos (totales)</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937' }}>{progressiveTotalsSinceApproval}</span>
          </div>
        </div>
      </div>
      
      <CardContent style={{ padding: '16px' }}>
        {/* Botones de Acción */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Button 
            onClick={() => setVacationRequestOpen(true)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563EB'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3B82F6'}
          >
            <Plus size={16} />
            Solicitar Vacaciones
          </Button>

          <Button 
            onClick={() => setAdministrativeRequestOpen(true)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
          >
            <Plus size={16} />
            Días Administrativos
          </Button>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setProgressiveDaysOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                backgroundColor: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#D1D5DB';
              }}
            >
              <Plus size={14} />
              Progresivos
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onAddDays?.(employee.id || employee._id, 'additional')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                backgroundColor: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#D1D5DB';
              }}
            >
              <Plus size={14} />
              Adicionales
            </Button>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onViewDetails?.(employee.id || employee._id)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              padding: '6px 12px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: '#6B7280',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#F9FAFB';
              e.target.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6B7280';
            }}
          >
            <FileText size={14} />
            Ver Informe Detallado
          </Button>
        </div>
      </CardContent>

      {/* Modals */}
      <VacationRequestDialog 
        employee={employee} 
        open={vacationRequestOpen}
        onOpenChange={setVacationRequestOpen}
        onRequestCreated={handleRequestCreated}
      />

      <AdministrativeRequestDialog 
        employee={employee} 
        open={administrativeRequestOpen}
        onOpenChange={setAdministrativeRequestOpen}
        onRequestCreated={handleRequestCreated}
      />
      
      <VoucherModal 
        request={currentRequest}
        open={voucherModalOpen}
        onOpenChange={setVoucherModalOpen}
      />

      <ProgressiveDaysDialog
        employee={employee}
        open={progressiveDaysOpen}
        onOpenChange={setProgressiveDaysOpen}
        onUpdateEmployee={onUpdateEmployee}
      />

      <EditEmployeeDialog
        employee={employee}
        open={editEmployeeOpen}
        onOpenChange={setEditEmployeeOpen}
        onEmployeeUpdated={onUpdateEmployee}
      />
    </Card>
  );
};
